import { newField, uid, type Project } from "@proof/core";
import type { ConfigurationField } from "./configuration-fields";

/** Editor hints are separate from document data; callers can replace them through JavaScript. */
export function projectEditorFields(project: Project) {
  const variables = project.fields.map((field) => ({
    label: `${field.label} (${field.key})`,
    value: field.id,
  }));
  const variable: ConfigurationField = { type: "string", choices: variables };
  const variableList: ConfigurationField = { type: "array", items: variable };
  const groups: ConfigurationField = {
    type: "array",
    items: {
      type: "string",
      choices: project.groups.map((group) => ({
        label: group.name,
        value: group.id,
      })),
    },
  };
  const condition: ConfigurationField = {
    properties: {},
    additionalProperties: false,
    create: () => ({
      field: project.fields[0]?.key || "",
      op: "eq",
      value: "",
    }),
  };
  condition.properties = {
    field: {
      title: "Source variable",
      type: "string",
      choices: project.fields.map((field) => ({
        label: `${field.label} (${field.key})`,
        value: field.key,
      })),
    },
    op: {
      title: "Comparison",
      type: "string",
      choices: [
        ["eq", "Equals"],
        ["neq", "Does not equal"],
        ["empty", "Is empty"],
        ["notEmpty", "Has a value"],
        ["startsWith", "Starts with"],
        ["endsWith", "Ends with"],
        ["contains", "Contains"],
        ["notContains", "Does not contain"],
        ["matches", "Matches expression"],
        ["gt", "Greater than"],
        ["lt", "Less than"],
        ["in", "In list"],
      ].map(([value, label]) => ({ value, label })),
    },
    value: { title: "Comparison value" },
    all: {
      title: "All conditions must match",
      type: "array",
      items: condition,
    },
    any: { title: "Any condition may match", type: "array", items: condition },
  };
  const trigger: ConfigurationField = {
    type: "object",
    additionalProperties: false,
    create: () => ({
      id: uid("trigger"),
      sourceId: project.fields[0]?.id || "",
      value: "",
      show: [],
      hide: [],
      clear: [],
      exclude: {},
    }),
    properties: {
      id: { title: "Rule ID", type: "string" },
      sourceId: { ...variable, title: "Source variable" },
      op: {
        title: "Match operator",
        type: "string",
        choices: ["eq", "startsWith", "endsWith", "contains", "matches"].map(
          (value) => ({ value, label: value }),
        ),
      },
      value: { title: "Matching value" },
      show: {
        ...variableList,
        title: "Show variables or groups",
        items: {
          type: "string",
          choices: [
            ...variables,
            ...project.groups.map((group) => ({
              label: `Group: ${group.name}`,
              value: group.id,
            })),
          ],
        },
      },
      hide: {
        ...variableList,
        title: "Hide variables or groups",
        items: {
          type: "string",
          choices: [
            ...variables,
            ...project.groups.map((group) => ({
              label: `Group: ${group.name}`,
              value: group.id,
            })),
          ],
        },
      },
      clear: { ...variableList, title: "Clear variables" },
      exclude: {
        title: "Excluded choices by variable ID",
        type: "object",
        additionalProperties: { type: "array", items: { type: "string" } },
      },
    },
  };
  return {
    condition,
    triggers: { type: "array", items: trigger } satisfies ConfigurationField,
    tabs: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        create: () => ({
          id: uid("tab"),
          name: "New tab",
          order: project.tabs.length,
          groupIds: [],
        }),
        properties: {
          id: { title: "Tab ID", type: "string" },
          name: { title: "Tab name", type: "string" },
          order: { title: "Display order", type: "number" },
          groupIds: { ...groups, title: "Form groups" },
        },
      },
    } satisfies ConfigurationField,
    forms: {
      type: "array",
      items: {
        type: "object",
        create: () => ({
          id: uid("form"),
          name: "New form",
          fields: [newField("text")],
          allowCreate: true,
        }),
        properties: {
          id: { title: "Form ID", type: "string" },
          name: { title: "Form name", type: "string" },
          allowCreate: { title: "Allow new profiles", type: "boolean" },
          fields: {
            title: "Form inputs",
            type: "array",
            items: {
              type: "object",
              create: () => newField("text"),
            },
          },
        },
      },
    } satisfies ConfigurationField,
    metadata: {
      type: "object",
      additionalProperties: { type: "string" },
    } satisfies ConfigurationField,
  };
}
